"use client";

import React, { useRef, useState, useCallback } from 'react';
import styles from './_gc-step-patient-expectations.module.scss';
import GcStepShell from '../../components/gc-step-shell';
import { FormInputCalendar, FormInputCheckbox, FormInputTimePicker } from '@/components/form';
import { validatePastDate } from '../../utils';
import { GC_CALC_LAB_DRAW_TIMES } from '@/data/gc-calculator-data';

const DISCUSSION_POINTS = [
  'Lab tests, performed at a consistent time of day, are required to advise GC dose reductions',
  'The need to continue to receive GCs for cortisol deficiency',
  'Symptoms of GC withdrawal may occur, but are usually manageable or resolve on their own',
  'Adjustments to fludrocortisone supplementation may be needed',
  'The amount of cortisol needed and timing of administration for stress dosing should remain the same as before the reduction; clear instructions should be provided',
];

const LAB_DRAW_TIMES = GC_CALC_LAB_DRAW_TIMES;

/**
 * @description GC OPTIMIZE™ Calculator — Step 2: Patient Expectations
 * Displays expectations copy + discussion points checklist, collects
 * most recent lab draw date (FPO — calendar to be added) and lab draw time.
 *
 * Figma: 7:2429 (starting/adult), 7:3631 (continuing/pediatric)
 */
function GcStepPatientExpectations({ state, dispatch, onBack, onNext, isReview, stepNumber }) {
  // ── Refs (no re-render on change) ─────────────────────────────────
  const dateRef       = useRef(state.formValues.labDrawDate ?? '');
  const visitDateRef  = useRef(state.formValues.visitDate ?? '');
  const labTimeRef    = useRef(state.formValues.labDrawTime ?? '09:30');
  const labRelativeRef = useRef(state.formValues.labDrawRelative ?? '');
  const calendarRef   = useRef(null);
  const visitCalRef   = useRef(null);
  const timePickerRef = useRef(null);

  // ── Single state: button enabled/disabled ─────────────────────────
  const [isNextDisabled, setIsNextDisabled] = useState(
    () => !dateRef.current || !labTimeRef.current || !labRelativeRef.current
  );

  const validate = useCallback(() => {
    const val = dateRef.current;

    // Validate both date inputs every time so both error states can render together.
    const labDateValid = !val || validatePastDate(val, calendarRef);
    const visitDateValid = !visitDateRef.current || validatePastDate(visitDateRef.current, visitCalRef);
    const hasRequiredFields = !!val && !!labTimeRef.current && !!labRelativeRef.current;

    setIsNextDisabled(!labDateValid || !visitDateValid || !hasRequiredFields);
  }, []);

  const handleSave = useCallback(() => {
    const labDateValid = !dateRef.current || validatePastDate(dateRef.current, calendarRef);
    const visitDateValid = !visitDateRef.current || validatePastDate(visitDateRef.current, visitCalRef);
    const hasRequiredFields = !!dateRef.current && !!labTimeRef.current && !!labRelativeRef.current;

    if (!labDateValid || !visitDateValid || !hasRequiredFields) {
      setIsNextDisabled(true);
      return;
    }

    dispatch({ type: 'SET_FIELD', field: 'visitDate', value: visitDateRef.current });
    dispatch({ type: 'SET_FIELD', field: 'labDrawDate', value: dateRef.current });
    dispatch({ type: 'SET_FIELD', field: 'labDrawTime', value: labTimeRef.current });
    dispatch({ type: 'SET_FIELD', field: 'labDrawRelative', value: labRelativeRef.current });
    onNext();
  }, [dispatch, onNext]);

  return (
    <GcStepShell
      title="Patient Expectations"
      onBack={onBack}
      onNext={handleSave}
      showBack={false}
      isNextDisabled={isNextDisabled}
      isReview={isReview}
      stepNumber={stepNumber}
    >
      {/* ── Info copy ────────────────────────────────────────── */}
      <div className={styles.info_copy}>
        <p className={styles.paragraph}>
          Establishing expectations with your patient early may help reduce hesitation, improve
          adherence, and make the GC dose reduction experience smoother. It&rsquo;s good
          for patients to be reminded about lab timings, potential symptoms of GC
          withdrawal, the need to continue GCs for cortisol deficiency, fludrocortisone
          adjustments, and stress dosing instructions.
        </p>
        <p className={styles.paragraph}>
          Before initiating GC dose reduction, discuss the following expectations with
          the patient:
        </p>
        <ul className={styles.discussion_list}>
          {DISCUSSION_POINTS.map((point, i) => (
            <li key={i}>{point}</li>
          ))}
        </ul>
      </div>

      {/* ── Most recent visit date ──────────────────────────────────────── */}
      <FormInputCalendar
        ref={visitCalRef}
        name="gc-visit-date"
        label="Most recent visit date"
        required
        disableDates="future"
        defaultValue={state.formValues.visitDate ?? ''}
        yearDropdownYearsBack={5}
        customClass={styles.date_input}
        errorMessage="Please select a past date."
        onChange={(e) => { visitDateRef.current = e.target.value; validate(); }}
        onBlur={(e) => { visitDateRef.current = e.target.value; validate(); }}
      />

      {/* ── Most recent lab draw date ───────────────────────────────────────── */}
      <FormInputCalendar
        ref={calendarRef}
        name="gc-lab-draw-date"
        label="Most recent lab draw date"
        required
        disableDates="future"
        defaultValue={state.formValues.labDrawDate ?? ''}
        yearDropdownYearsBack={5}
        customClass={styles.date_input}
        // errorMessage="Please select a date within the past 5 years."
        errorMessage="Please select a past date."
        onChange={(e) => { dateRef.current = e.target.value; validate(); }}
        onBlur={(e) => { dateRef.current = e.target.value; validate(); }}
      />

      {/* ── Lab draw time ──────────────────────────────────────── */}
      <div className={styles.time_picker_section}>
        <FormInputTimePicker
          ref={timePickerRef}
          name="gc-lab-draw-time"
          label="Lab draw time"
          onChange={({ currentValue }) => { labTimeRef.current = currentValue; validate(); }}
        />
      </div>

      {/* ── Lab draw relative to GCs ────────────────────────────── */}
      <fieldset className={styles.field_group}>
        <legend className={`${styles.field_label} !mb-6`}>Lab draw relative to GCs</legend>
        {LAB_DRAW_TIMES.map(({ id, label }) => (
          <FormInputCheckbox
            key={id}
            name={`gc-lab-time-${id}`}
            typeRadio
            radioGroupName="gc-lab-time"
            defaultChecked={labRelativeRef.current === id}
            onChange={() => { labRelativeRef.current = id; validate(); }}
          >
            {label}
          </FormInputCheckbox>
        ))}
      </fieldset>

      <p className={styles.note}>
        Consistent timing relative to GC dose is important to making meaningful
        comparisons across visits.
      </p>
    </GcStepShell>
  );
}

export default GcStepPatientExpectations;
